import React from 'react';
import { Link } from 'react-router-dom';
import './EmptyState.css';

interface EmptyStateAction {
  label: string;
  onClick?: () => void;
  href?: string;
}

interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: EmptyStateAction;
  size?: 'sm' | 'md' | 'lg';
}

const sizeConfig = {
  sm: {
    padding: '1.5rem',
    iconSize: '2rem',
    titleSize: '1rem',
    descriptionSize: '0.875rem',
    buttonPadding: '0.5rem 1rem',
    buttonFontSize: '0.875rem',
    gap: '0.75rem',
  },
  md: {
    padding: '2.5rem',
    iconSize: '3rem',
    titleSize: '1.25rem',
    descriptionSize: '1rem',
    buttonPadding: '0.75rem 1.5rem',
    buttonFontSize: '1rem',
    gap: '1rem',
  },
  lg: {
    padding: '4rem 2rem',
    iconSize: '4rem',
    titleSize: '1.5rem',
    descriptionSize: '1.125rem',
    buttonPadding: '1rem 2rem',
    buttonFontSize: '1.1rem',
    gap: '1.25rem',
  },
};

const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  action,
  size = 'md',
}) => {
  const config = sizeConfig[size];

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: config.padding,
    gap: config.gap,
  };

  const iconContainerStyle: React.CSSProperties = {
    color: 'var(--text-muted)',
    fontSize: config.iconSize,
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const titleStyle: React.CSSProperties = {
    color: 'var(--text-primary)',
    fontSize: config.titleSize,
    fontWeight: 600,
    margin: 0,
  };

  const descriptionStyle: React.CSSProperties = {
    color: 'var(--text-secondary)',
    fontSize: config.descriptionSize,
    margin: 0,
    maxWidth: '400px',
    lineHeight: 1.5,
  };

  const buttonStyle: React.CSSProperties = {
    padding: config.buttonPadding,
    fontSize: config.buttonFontSize,
  };

  const renderAction = () => {
    if (!action) return null;

    if (action.href) {
      return (
        <Link
          to={action.href}
          className="empty-state-action"
          style={buttonStyle}
        >
          {action.label}
        </Link>
      );
    }

    if (action.onClick) {
      return (
        <button
          type="button"
          className="empty-state-action"
          style={buttonStyle}
          onClick={action.onClick}
        >
          {action.label}
        </button>
      );
    }

    return null;
  };

  return (
    <div style={containerStyle}>
      {icon && <div style={iconContainerStyle}>{icon}</div>}
      <h3 style={titleStyle}>{title}</h3>
      {description && <p style={descriptionStyle}>{description}</p>}
      {renderAction()}
    </div>
  );
};

export default EmptyState;
